import React, { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface InlineEditableDropdownProps {
  value: string;
  options: { value: string; label: string }[];
  onSave: (newValue: string) => Promise<void>;
  className?: string;
  disabled?: boolean;
  renderValue?: (value: string) => React.ReactNode;
}

export function InlineEditableDropdown({
  value,
  options,
  onSave,
  className = '',
  disabled = false,
  renderValue,
}: InlineEditableDropdownProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        if (isEditing && editValue !== value) {
          handleSave(editValue);
        } else {
          setIsEditing(false);
        }
      }
    };

    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isEditing, editValue, value]);

  const handleDoubleClick = () => {
    if (disabled) return;
    setIsEditing(true);
  };

  const handleSave = async (newValue: string) => {
    if (newValue === value) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(newValue);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save:', error);
      // Reset to original value on error
      setEditValue(value);
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (newValue: string) => {
    setEditValue(newValue);
    handleSave(newValue);
  };

  const displayValue = renderValue ? renderValue(value) : value;

  if (isEditing) {
    return (
      <div ref={containerRef} className={className}>
        <Select
          value={editValue}
          onValueChange={handleChange}
          disabled={isSaving}
        >
          <SelectTrigger className="w-full h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  return (
    <div
      onDoubleClick={handleDoubleClick}
      className={cn(
        'px-2 py-1 rounded transition-colors',
        !disabled && 'cursor-pointer hover:bg-gray-100',
        disabled && 'cursor-not-allowed opacity-50',
        className
      )}
      title={disabled ? undefined : 'Double-click to edit'}
    >
      {displayValue}
    </div>
  );
}